/**
 * Ordinal train layout.
 *
 * A component's place in the scene is its index in the machine's component order
 * (`components.position`) — no geometry is stored per component. The train is laid out by
 * walking that order with a single cursor, seating each bounding box flush against its
 * predecessor.
 *
 * Returns the target world-space AABB *centre* along the train axis, not a root origin:
 * procedural models are authored around arbitrary origins, so centres are the only thing that
 * makes two adjacent boxes reliably touch. The caller translates each root by
 * `target - currentBBoxCentre`.
 */

export const TRAIN_GAP = 0;

/** Horizontal machines run their train along world X; vertical machines stack along world Y. */
export function getTrainAxis(orientation) {
  return orientation === 'vertical' ? 'y' : 'x';
}

function usableEntries(entries) {
  return (entries || []).filter(
    entry => entry
      && entry.id != null
      && Number.isFinite(entry.length)
      && entry.length > 0
  );
}

/**
 * Target world-space AABB centre along the train axis for every component, in train order.
 *
 * Horizontal trains are centred on the origin, so adding a component re-centres the run rather
 * than growing it off to one side. Vertical trains instead rest on the floor with index 0 (the
 * driver) at the top, matching a vertical agitator or pump drive.
 */
export function planTrainLayout(entries, opts) {
  const options = opts || {};
  const gap = options.gap != null ? options.gap : TRAIN_GAP;
  const isVertical = options.orientation === 'vertical';
  const usable = usableEntries(entries);
  const centers = new Map();
  if (!usable.length) return centers;

  const total = usable.reduce(
    (sum, entry, index) => sum + entry.length + (index > 0 ? gap : 0),
    0
  );

  if (isVertical) {
    let cursor = total;
    usable.forEach((entry) => {
      cursor -= entry.length;
      centers.set(String(entry.id), cursor + (entry.length / 2));
      cursor -= gap;
    });
    return centers;
  }

  let cursor = -total / 2;
  usable.forEach((entry) => {
    centers.set(String(entry.id), cursor + (entry.length / 2));
    cursor += entry.length + gap;
  });
  return centers;
}

/**
 * Lift every component onto one continuous shaft centreline, returning deltas rather than
 * mutating so the same plan can be applied to the scene and to a drag ghost.
 *
 * Floor-snapping each component individually leaves its shaft at whatever height that model's
 * base-to-shaft distance implies, so adjacent shafts sit at different heights and the coupling
 * between them looks bent. Aligning to the HIGHEST shaft makes every correction a lift, so no
 * component is pushed below the floor.
 */
export function planShaftAlignment(entries) {
  const usable = (entries || []).filter(
    e => e && e.id != null && Number.isFinite(e.shaftWorldY)
  );
  const deltas = new Map();
  if (usable.length < 2) return deltas;
  const targetY = Math.max(...usable.map(e => e.shaftWorldY));
  usable.forEach((e) => {
    const delta = targetY - e.shaftWorldY;
    if (delta !== 0) deltas.set(e.id, delta);
  });
  return deltas;
}

/**
 * Index of the component the train STEPS DOWN at, or -1 when it stays on one centreline.
 *
 * A parallel-shaft gearbox keeps the run straight but hands it over on a lower shaft. Whether
 * the shafts are offset is a property of the BODY (`outputShaftAxisY` in the model registry),
 * so `stepFor` is injected to keep this module free of the registry. A pivot that is LAST
 * returns -1.
 */
export function findShaftStepPivotIndex(entries, stepFor) {
  const list = entries || [];
  for (let i = 0; i < list.length; i += 1) {
    const entry = list[i];
    if (entry && entry.id != null && stepFor(entry.registryKey)) {
      return i === list.length - 1 ? -1 : i;
    }
  }
  return -1;
}

/**
 * Split the train into the run on the input centreline (including the pivot) and the run that
 * hands off on the pivot's output shaft. The pivot belongs to the UPSTREAM run — it is driven
 * by it. With no step, `upstream` is the whole train and `downstream` is empty.
 */
export function splitTrainAtShaftStep(entries, stepFor) {
  const list = entries || [];
  const index = findShaftStepPivotIndex(list, stepFor);
  if (index < 0) return { pivotId: null, upstream: list.slice(), downstream: [] };
  return {
    pivotId: String(list[index].id),
    upstream: list.slice(0, index + 1),
    downstream: list.slice(index + 1),
  };
}

/* ------------------------------------------------------------------------------------------
 * Mid-train direction change ("elbow")
 *
 * A right-angle multi-stage gearbox turns the train: input along the horizontal run, output
 * downward, so everything downstream hangs from a vertical shaft. This mirrors the 2D machine
 * graphic (`MachineOverview/components/GraphicsV2.js`, `applyMixedLayout`) — the two views
 * must agree. Data-driven off the gearbox's `orientation` component property.
 * ---------------------------------------------------------------------------------------- */

/**
 * The only registry keys that can turn the train; both are still gated on
 * `properties.orientation === 'vertical'`.
 *
 * Deliberately narrow: pumps, compressors and the jaw crusher carry a similar
 * `shaft_orientation` property, but a vertical pump is a body standing on end at the END of
 * a run — it does not redirect the components after it.
 */
export const ELBOW_PIVOT_REGISTRY_KEYS = [
  'multi_stage_gearbox',
  'multi_stage_gearbox_vertical',
];

export const ELBOW_ORIENTATION_PROPERTY = 'orientation';
export const ELBOW_ORIENTATION_VALUE = 'vertical';

/**
 * Which way the downstream run leaves the elbow: +1 = up (+Y), -1 = down (-Y).
 *
 * Down, matching what the equipment does and what `GraphicsV2.applyMixedLayout` draws in 2D.
 * The hanging run would go through the floor if each component were ground-snapped on its
 * own, so it is not — the whole assembly is lifted in one pass afterwards.
 */
export const ELBOW_DIRECTION = -1;

/**
 * Index of the component that turns the train, or -1 when the train stays straight.
 *
 * A pivot is a multi-stage-gearbox model carrying `properties.orientation === 'vertical'`;
 * the first one wins. This takes precedence over `machine.orientation` (as it does in 2D),
 * which is why no machine orientation is passed in. A pivot that is LAST returns -1: there
 * is nothing downstream to redirect.
 */
export function findElbowPivotIndex(entries) {
  const list = entries || [];
  for (let i = 0; i < list.length; i += 1) {
    const entry = list[i];
    const properties = (entry && entry.properties) || null;
    if (
      entry
      && entry.id != null
      && ELBOW_PIVOT_REGISTRY_KEYS.indexOf(entry.registryKey) > -1
      && properties
      && properties[ELBOW_ORIENTATION_PROPERTY] === ELBOW_ORIENTATION_VALUE
    ) {
      return i === list.length - 1 ? -1 : i;
    }
  }
  return -1;
}

/**
 * Split the train into the segment that stays horizontal and the segment that stands up.
 * The pivot belongs to the HORIZONTAL segment — it is driven by that run, so its input shaft
 * stays on that centreline. With no elbow the horizontal segment is the whole train.
 */
export function splitTrainAtElbow(entries) {
  const list = entries || [];
  const pivotIndex = findElbowPivotIndex(list);
  if (pivotIndex < 0) {
    return {
      pivotIndex: -1, pivotId: null, horizontal: list.slice(), vertical: [],
    };
  }
  return {
    pivotIndex,
    pivotId: list[pivotIndex].id,
    horizontal: list.slice(0, pivotIndex + 1),
    vertical: list.slice(pivotIndex + 1),
  };
}

/**
 * Target world-space AABB centre along Y for each component downstream of the elbow.
 *
 * Unlike `planTrainLayout` this run is neither centred nor floor-seated: it hangs off the
 * BOTTOM face of the pivot's bounding box (`start`), because that is where the downward
 * output shaft leaves. The floor is dealt with afterwards by lifting both runs together
 * (`SceneManager.seatElbowedTrainOnFloor`). Only the sign of `direction` is read.
 */
export function planElbowSegmentLayout(entries, opts) {
  const options = opts || {};
  const gap = options.gap != null ? options.gap : TRAIN_GAP;
  const start = Number.isFinite(options.start) ? options.start : 0;
  const direction = Number.isFinite(options.direction) ? options.direction : ELBOW_DIRECTION;
  const sign = direction < 0 ? -1 : 1;

  const centers = new Map();
  let cursor = start + (sign * gap);
  usableEntries(entries).forEach((entry) => {
    centers.set(String(entry.id), cursor + ((sign * entry.length) / 2));
    cursor += sign * (entry.length + gap);
  });
  return centers;
}
